import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Shield as RoleIcon,
} from '@mui/icons-material';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTablePagination,
  Table,
  TableCell,
  TableRow,
} from '../../../components/StyledTable';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive';
  lastLogin: string;
}

interface Role {
  id: number;
  name: string;
  description: string;
  pages: string[];
  color: string;
}

interface Page {
  id: string;
  name: string;
  path: string;
  category: string;
}

const UsersTab = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Available pages that can be assigned to roles
  const availablePages: Page[] = [
    { id: 'dashboard', name: 'Dashboard', path: '/', category: 'Main' },
    { id: 'insights', name: 'Insights', path: '/insights', category: 'Analytics' },
    { id: 'ads-manager', name: 'Ads Manager', path: '/ads-manager', category: 'Campaigns' },
    { id: 'studio', name: 'Studio', path: '/studio', category: 'Creative' },
    { id: 'campaign-analytics', name: 'Campaign Analytics', path: '/campaign-analytics', category: 'Analytics' },
    { id: 'optimizer', name: 'Optimizer', path: '/optimizer', category: 'Optimization' },
    { id: 'rules', name: 'Rules', path: '/rules', category: 'Automation' },
    { id: 'rules-dashboard', name: 'Rules Dashboard', path: '/rules-dashboard', category: 'Automation' },
    { id: 'rules-explorer', name: 'Rules Explorer', path: '/rules-explorer', category: 'Automation' },
    { id: 'campaign-genius', name: 'Campaign Genius', path: '/campaign-genius', category: 'AI Tools' },
    { id: 'media-library', name: 'Media Library', path: '/media-library', category: 'Assets' },
    { id: 'scraper', name: 'Scraper', path: '/scraper', category: 'Tools' },
    { id: 'rsoc-keywords', name: 'RSOC Keywords', path: '/rsoc-keywords', category: 'Tools' },
    { id: 'compliance', name: 'Compliance', path: '/compliance', category: 'Management' },
    { id: 'admin', name: 'Admin Console', path: '/admin', category: 'Administration' },
  ];

  // Mock roles data
  const [roles, setRoles] = useState<Role[]>([
    {
      id: 1,
      name: 'Admin',
      description: 'Full system access',
      pages: availablePages.map(p => p.id),
      color: theme.palette.error.main,
    },
    {
      id: 2,
      name: 'Manager',
      description: 'Campaign and analytics access',
      pages: ['dashboard', 'insights', 'ads-manager', 'campaign-analytics', 'optimizer', 'rules'],
      color: theme.palette.primary.main,
    },
    {
      id: 3,
      name: 'Analyst',
      description: 'Read-only analytics access',
      pages: ['dashboard', 'insights', 'campaign-analytics', 'rules-dashboard'],
      color: theme.palette.info.main,
    },
    {
      id: 4,
      name: 'Viewer',
      description: 'Basic dashboard access',
      pages: ['dashboard'],
      color: theme.palette.text.secondary,
    },
  ]);

  // Mock users data
  const [users, setUsers] = useState<User[]>([
    { id: 1, name: 'John Admin', email: 'admin@example.com', role: 'Admin', status: 'active', lastLogin: '2025-12-16 09:30' },
    { id: 2, name: 'Sarah Manager', email: 'sarah@example.com', role: 'Manager', status: 'active', lastLogin: '2025-12-16 08:15' },
    { id: 3, name: 'Mike Analyst', email: 'mike@example.com', role: 'Analyst', status: 'active', lastLogin: '2025-12-15 17:45' },
    { id: 4, name: 'Jane Viewer', email: 'jane@example.com', role: 'Viewer', status: 'active', lastLogin: '2025-12-15 10:20' },
    { id: 5, name: 'Bob Smith', email: 'bob@example.com', role: 'Manager', status: 'inactive', lastLogin: '2025-12-10 14:30' },
  ]);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'Viewer',
  });

  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    pages: [] as string[],
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenUserDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setNewUser({
        name: user.name,
        email: user.email,
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setNewUser({
        name: '',
        email: '',
        role: 'Viewer',
      });
    }
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
  };

  const handleSaveUser = () => {
    if (editingUser) {
      // Update existing user
      setUsers(users.map(u =>
        u.id === editingUser.id
          ? { ...u, name: newUser.name, email: newUser.email, role: newUser.role }
          : u
      ));
    } else {
      // Add new user
      const newId = Math.max(...users.map(u => u.id)) + 1;
      setUsers([...users, {
        id: newId,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        status: 'active',
        lastLogin: 'Never',
      }]);
    }
    handleCloseUserDialog();
  };

  const handleDeleteUser = (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== userId));
    }
  };

  const handleOpenRoleDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setNewRole({
        name: role.name,
        description: role.description,
        pages: role.pages,
      });
    } else {
      setEditingRole(null);
      setNewRole({
        name: '',
        description: '',
        pages: [],
      });
    }
    setRoleDialogOpen(true);
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
  };

  const handleSaveRole = () => {
    const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.info.main, theme.palette.success.main];

    if (editingRole) {
      // Update existing role
      setRoles(roles.map(r =>
        r.id === editingRole.id
          ? { ...r, name: newRole.name, description: newRole.description, pages: newRole.pages }
          : r
      ));
    } else {
      // Add new role
      const newId = Math.max(...roles.map(r => r.id)) + 1;
      setRoles([...roles, {
        id: newId,
        name: newRole.name,
        description: newRole.description,
        pages: newRole.pages,
        color: colors[newId % colors.length],
      }]);
    }
    handleCloseRoleDialog();
  };

  const handleDeleteRole = (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    const usersWithRole = users.filter(u => u.role === role?.name);

    if (usersWithRole.length > 0) {
      alert(`Cannot delete role "${role?.name}". ${usersWithRole.length} user(s) are assigned to this role.`);
      return;
    }

    if (window.confirm('Are you sure you want to delete this role?')) {
      setRoles(roles.filter(r => r.id !== roleId));
    }
  };

  const handleTogglePage = (pageId: string) => {
    if (newRole.pages.includes(pageId)) {
      setNewRole({ ...newRole, pages: newRole.pages.filter(p => p !== pageId) });
    } else {
      setNewRole({ ...newRole, pages: [...newRole.pages, pageId] });
    }
  };

  const getRoleColor = (roleName: string) => {
    const role = roles.find(r => r.name === roleName);
    return role?.color || theme.palette.text.secondary;
  };

  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Group pages by category
  const pagesByCategory = availablePages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, Page[]>);

  return (
    <Box>
      {/* Users Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Users
          </Typography>
          <Button
            startIcon={<PersonAddIcon />}
            variant="contained"
            onClick={() => handleOpenUserDialog()}
          >
            Add User
          </Button>
        </Box>

        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </StyledTableHead>

            <StyledTableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={{
                        backgroundColor: `${getRoleColor(user.role)}15`,
                        color: getRoleColor(user.role),
                        fontWeight: 600,
                        fontSize: '12px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      size="small"
                      sx={{
                        backgroundColor: user.status === 'active'
                          ? `${theme.palette.success.main}15`
                          : `${theme.palette.text.secondary}15`,
                        color: user.status === 'active'
                          ? theme.palette.success.main
                          : theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: '12px',
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
                    {user.lastLogin}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenUserDialog(user)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                      sx={{ color: theme.palette.error.main, ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>

        <StyledTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {/* Roles Section */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Roles
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => handleOpenRoleDialog()}
          >
            Create Role
          </Button>
        </Box>

        <Grid container spacing={3}>
          {roles.map((role) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={role.id}>
              <Card
                sx={{
                  borderRadius: '10px',
                  boxShadow: '0 5px 15px rgba(146,153,184,0.1)',
                  border: 'none',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: `${role.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: role.color,
                      }}
                    >
                      <RoleIcon />
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRoleDialog(role)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRole(role.id)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {role.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {role.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role.pages.length} pages assigned
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {users.filter(u => u.role === role.name).length} users
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              fullWidth
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseUserDialog}>Cancel</Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={!newUser.name || !newUser.email}
          >
            {editingUser ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={handleCloseRoleDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Role Name"
              fullWidth
              value={newRole.name}
              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Assign Pages
              </Typography>

              {Object.entries(pagesByCategory).map(([category, pages]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {category}
                  </Typography>
                  <FormGroup>
                    <Grid container spacing={1}>
                      {pages.map((page) => (
                        <Grid item xs={12} sm={6} key={page.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newRole.pages.includes(page.id)}
                                onChange={() => handleTogglePage(page.id)}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&.Mui-checked': {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              />
                            }
                            label={page.name}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                              }
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.grey[50],
                borderRadius: '8px',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Selected Pages: {newRole.pages.length}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {newRole.pages.map((pageId) => {
                  const page = availablePages.find(p => p.id === pageId);
                  return page ? (
                    <Chip
                      key={pageId}
                      label={page.name}
                      size="small"
                      onDelete={() => handleTogglePage(pageId)}
                      sx={{
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ) : null;
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseRoleDialog}>Cancel</Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            disabled={!newRole.name || !newRole.description || newRole.pages.length === 0}
          >
            {editingRole ? 'Save Changes' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTab;
